import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { useState } from 'react'
import { Brand } from '@/components/Brand'
import { Button, buttonVariants } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { sessionQuery } from '@/lib/queries'
import { cn } from '@/lib/utils'

/**
 * Public landing + sign-in — the only page outside the authenticated /app shell. A two-column split
 * (ported from the sign-in artifact): the brand statement on the left, the auth card on the right,
 * stacking to one column on phones. Everything is semantic tokens, so it follows the system theme
 * like the rest of the app rather than carrying its own palette.
 *
 * The card has two modes because the two actions are genuinely different. SIGN IN is a plain
 * navigation to the API's AuthKit entry point — there is no auth SDK in this bundle; WorkOS's hosted
 * page is where a password or SSO is entered, so there is nothing to type here first. REQUEST ACCESS
 * exists because Clipbook is invite-only: there is no self-serve sign-up, so a prospective user
 * leaves a work email that hands off to the marketing site rather than creating an account that would
 * only land on the "ask an administrator" screen. Signed-in visitors instead get a straight "Open
 * app" path, and an authenticated-but-unadmitted visitor is told to ask for an invitation.
 *
 * The signed-in / signed-out / no-access split resolves in the loader, so the right card is in the
 * first paint of this route.
 */
export const Route = createFileRoute('/')({
  loader: ({ context }) => context.queryClient.ensureQueryData(sessionQuery),
  component: Landing,
})

/** The words that cycle after "AI research and analysis for" — the clipbook.io line. The first is
 *  repeated at the end so the CSS loop lands back on it seamlessly (word-rotate in styles.css). */
const ROTATING = [
  'public relations',
  'communications',
  'corporate affairs',
  'public affairs',
  'enterprise',
] as const

/** Straight into AuthKit's hosted sign-in; WorkOS handles SSO and email from there. */
const SIGN_IN_HREF = '/api/auth/sign-in?returnPathname=%2Fapp'

/** Where "Request access" hands off — the marketing site, since there is no in-app sign-up. The work
 *  email rides along best-effort; clipbook.io is free to use or ignore it. */
function requestAccessHref(email: string): string {
  const trimmed = email.trim()
  return trimmed
    ? `https://www.clipbook.io/?email=${encodeURIComponent(trimmed)}`
    : 'https://www.clipbook.io'
}

function Landing() {
  const { user, noAccess } = Route.useLoaderData()

  return (
    <div className="relative flex min-h-screen flex-wrap overflow-hidden bg-background text-foreground">
      {/* Two soft brand glows behind everything — token-based, so they tint gently in both themes. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute top-1/3 -left-40 size-[32rem] rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute -right-32 bottom-[8%] size-[32rem] rounded-full bg-primary/10 blur-3xl" />
      </div>

      {/* Left: the brand statement. */}
      <section className="relative flex min-w-0 flex-1 basis-[440px] flex-col justify-between gap-12 p-10 sm:p-14 lg:border-r lg:border-border">
        <a
          href="https://www.clipbook.io"
          aria-label="Clipbook home"
          className="w-fit rounded-md focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none"
        >
          <Brand size="lg" />
        </a>
        <h1 className="max-w-xl text-4xl leading-tight font-semibold tracking-tight text-balance sm:text-5xl">
          AI research and analysis for
          <br />
          <WordRotator />
        </h1>
        <p className="text-sm text-muted-foreground">© Clipbook</p>
      </section>

      {/* Right: the auth card. */}
      <section className="relative flex min-w-0 flex-1 basis-[440px] flex-col items-center justify-center gap-6 p-6 sm:p-14">
        <div className="w-full max-w-sm rounded-2xl border border-border bg-card/80 p-8 shadow-xl backdrop-blur">
          {user ? <SignedIn /> : noAccess ? <NoAccess /> : <AuthCard />}
        </div>
        <nav className="flex gap-4 text-xs text-muted-foreground">
          <a className="hover:text-foreground" href="https://www.clipbook.io">
            Privacy
          </a>
          <a className="hover:text-foreground" href="https://www.clipbook.io">
            Terms
          </a>
          <a className="hover:text-foreground" href="https://www.clipbook.io">
            Help
          </a>
        </nav>
      </section>
    </div>
  )
}

/** The rotating highlight in the headline. A vertical column of the words, clipped to one line tall,
 *  scrolled by the word-rotate keyframes; `motion-reduce:animate-none` pins it to the first word. */
function WordRotator() {
  return (
    <span className="inline-flex h-[1.1em] overflow-hidden align-bottom text-primary">
      <span className="flex animate-word-rotate flex-col motion-reduce:animate-none">
        {ROTATING.map((word) => (
          <span key={word} className="block h-[1.1em] leading-[1.1em] whitespace-nowrap">
            {word}
          </span>
        ))}
        {/* A repeat of the first word so the loop lands back on it seamlessly; hidden from the
            accessibility tree since it's the same word already announced. */}
        <span aria-hidden className="block h-[1.1em] leading-[1.1em] whitespace-nowrap">
          {ROTATING[0]}
        </span>
      </span>
    </span>
  )
}

/** The signed-out card: sign in (a button to WorkOS) or, for someone new, request access (a work
 *  email that hands off to the marketing site). Two modes toggled in place, not two forms at once. */
function AuthCard() {
  const [mode, setMode] = useState<'signin' | 'signup'>('signin')

  if (mode === 'signup') return <RequestAccessForm onBack={() => setMode('signin')} />

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-2xl font-semibold tracking-tight">Sign in</h2>
      <p className="text-sm text-muted-foreground">
        Use your work account — Clipbook signs you in through your organisation.
      </p>
      {/* A plain link, not a fetch: sign-in is a server round trip that ends at WorkOS's hosted page. */}
      <a href={SIGN_IN_HREF} className={cn(buttonVariants({ size: 'lg' }), 'mt-1 w-full')}>
        Sign in
      </a>
      <p className="text-sm text-muted-foreground">
        New to Clipbook?{' '}
        <button
          type="button"
          onClick={() => setMode('signup')}
          className="font-medium text-primary hover:underline"
        >
          Request access
        </button>
      </p>
    </div>
  )
}

/** Request access — the invite-only stand-in for sign-up. Captures a work email and hands off to the
 *  marketing site; it never creates an account (that would only reach the "no access" screen). */
function RequestAccessForm({ onBack }: { onBack: () => void }) {
  const [email, setEmail] = useState('')
  const [error, setError] = useState(false)

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-2xl font-semibold tracking-tight">Request access</h2>
      <p className="text-sm text-muted-foreground">
        Clipbook is invite-only. Leave your work email and the team will be in touch.
      </p>
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          if (!/.@./.test(email.trim())) {
            setError(true)
            return
          }
          // A full navigation, not SPA routing — this hands off to the marketing site.
          window.location.assign(requestAccessHref(email))
        }}
      >
        <label htmlFor="email" className="text-sm font-medium text-foreground">
          Work email
        </label>
        <Input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          placeholder="you@company.com"
          value={email}
          aria-invalid={error || undefined}
          onChange={(event) => {
            setEmail(event.target.value)
            if (error) setError(false)
          }}
        />
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            Enter your work email to continue.
          </p>
        ) : null}
        <Button type="submit" size="lg" className="mt-1">
          Request access
        </Button>
      </form>
      <p className="text-sm text-muted-foreground">
        Already have an account?{' '}
        <button type="button" onClick={onBack} className="font-medium text-primary hover:underline">
          Sign in
        </button>
      </p>
    </div>
  )
}

function SignedIn() {
  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-2xl font-semibold tracking-tight">Welcome back</h2>
      <p className="text-sm text-muted-foreground">You're signed in to Clipbook.</p>
      <Link to="/app" className={buttonVariants({ size: 'lg' })}>
        Open the app <ArrowRight className="size-4" />
      </Link>
    </div>
  )
}

function NoAccess() {
  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-2xl font-semibold tracking-tight">Almost there</h2>
      <p className="text-sm text-muted-foreground">
        You're signed in, but your account doesn't have access to this workspace yet. Ask an
        administrator to send you an invitation.
      </p>
      <a href="/api/auth/sign-out" className={buttonVariants({ variant: 'outline' })}>
        Sign out
      </a>
    </div>
  )
}
